import * as Sentry from "@sentry/nextjs";
import { format } from "date-fns";
import { IntegrationType } from "shared/integrations/integration";
import style from "./monday-list-item.module.css";
import classNames from "classnames";
import Checkbox from "frontend/ui-components/checkbox";
import { useDrag } from "react-dnd";
import { convertToLocalTime } from "shared/util/date-utils";
import { getPathPrefix } from "../../../utils/getPathPrefix";
import { getSelectedStatus } from "utils/monday-utils";

export function MondayListItem({
  item,
  integrationId,
  integrationType,
  configuration,
  onClick,
  isChecked,
  showCheckbox,
  onClickSubitems,
  onDragStart,
}: {
  item: any;
  integrationId: string;
  integrationType: IntegrationType;
  configuration: unknown;
  isChecked: boolean;
  onClick: () => void;
  showCheckbox: boolean;
  onClickSubitems?: () => void;
  onDragStart?: () => void;
}) {
  const [, drag] = useDrag(() => ({
    type: "monday-item",
    item: () => {
      onDragStart?.();
      return { item, integrationType, configuration, integrationId };
    },
    previewOptions: {
      captureDraggingState: true,
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  function iconForType() {
    switch (integrationType) {
      case IntegrationType.monday: {
        return (
          <svg width="16" height="9" viewBox="0 0 16 9" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g id="Group">
              <path
                id="Vector"
                d="M1.9074 8.90119C1.2174 8.90235 0.579514 8.54455 0.238708 7.96349C-0.0973057 7.38648 -0.0775402 6.67668 0.291416 6.11824L3.72762 0.893327C4.0858 0.321544 4.73387 -0.0194384 5.42387 0.000858106C6.11386 0.0170953 6.74097 0.391132 7.0656 0.980892C7.38604 1.56717 7.34591 2.27291 6.96198 2.82324L3.52757 8.04815C3.17359 8.58224 2.56266 8.90409 1.9074 8.90119Z"
                fill="#CBCFD2"
              />
              <path
                id="Vector_2"
                d="M7.80072 8.90144C7.10772 8.90144 6.46984 8.54248 6.13502 7.96606C5.79961 7.39021 5.81997 6.68273 6.18773 6.12545L9.61735 0.912715C9.97013 0.332813 10.62 -0.0162876 11.3154 0.00110946C12.0144 0.0161869 12.6475 0.394283 12.9679 0.986362C13.286 1.57844 13.2392 2.28824 12.8439 2.83741L9.4149 8.05014C9.06152 8.58249 8.45298 8.90318 7.80072 8.90144Z"
                fill="#CBCFD2"
              />
              <path
                id="Vector_3"
                d="M13.5633 8.94497C14.5404 8.94497 15.3326 8.19412 15.3326 7.2679C15.3326 6.34167 14.5404 5.59082 13.5633 5.59082C12.5861 5.59082 11.7939 6.34167 11.7939 7.2679C11.7939 8.19412 12.5861 8.94497 13.5633 8.94497Z"
                fill="#CBCFD2"
              />
            </g>
          </svg>
        );
      }
    }
  }

  function renderColumnValues() {
    if (!configuration) {
      return null;
    }
    const conf = configuration as any;

    switch (integrationType) {
      case IntegrationType.monday: {
        const dateColumn = conf.config.columns.find((column: any) => column.type === "date");
        const statusColumn = conf.columns.find((col: any) => col.type === "status");
        const dateColumnValue = dateColumn && item.columnValues.find((column: any) => column.id === dateColumn.id);
        const statusColumnValue = getSelectedStatus(item.columnValues, statusColumn);
        const dateRenderValue = dateColumnValue?.value?.date ?? dateColumnValue?.value;
        let formattedDate = null;
        try {
          if (dateColumnValue) {
            formattedDate = format(convertToLocalTime(new Date(dateRenderValue)), "MMM dd");
          }
        } catch (error) {
          Sentry.captureException({ error, dateRenderValue }, { tags: { type: "format-date" } });
        }
        return (
          <>
            {statusColumn && (
              <div className={style.statusValue} style={{ backgroundColor: statusColumnValue.color }}>
                {statusColumnValue.text}
              </div>
            )}
            {formattedDate && (
              <div className={style.dateValue}>
                <img src={getPathPrefix("/images/task-due-date-icon.svg")} />
                <div>{formattedDate}</div>
              </div>
            )}
          </>
        );
      }
    }
  }

  function subitemsClicked(e: any) {
    e.stopPropagation();
    onClickSubitems && onClickSubitems();
  }

  return (
    <div
      data-testid={item.name}
      onClick={onClick}
      className={classNames(style.taskContainer, { [style.checked]: isChecked })}
      ref={drag}
    >
      <div className={style.titleContainer}>
        <div className={style.title}>{item.name}</div>
        {showCheckbox && (
          <div className={style.checkboxContainer}>
            <Checkbox checked={isChecked} customStyle={{ transform: "scale(0.7)" }} />
          </div>
        )}
      </div>
      <div className={style.details}>
        {renderColumnValues()}
        <div className={style.integrationIcon}>{iconForType()}</div>
      </div>
      {item.subitems_ids.length > 0 && (
        <div className={style.subitemsCountContainer} onClick={subitemsClicked}>
          <div className={style.subitemsCount}>
            <svg width="12" height="12" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.28711 1V14.7138H9.00119" stroke="currentColor" strokeWidth="1.2" />
              <path
                d="M7.85742 6.71387H3.28616"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="square"
                strokeLinejoin="round"
              />
              <path
                d="M5.57129 1L1.00002 1"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="square"
                strokeLinejoin="round"
              />
              <path d="M9 5H16.9997V8.42845H9V5Z" stroke="currentColor" strokeWidth="1.2" />
              <path d="M9 13H16.9997V16.4284H9V13Z" stroke="currentColor" strokeWidth="1.2" />
            </svg>
            <span>
              Show {item.subitems_ids.length} {item.subitems_ids.length > 1 ? "subitems" : "subitem"}
            </span>
            <span>→</span>
          </div>
        </div>
      )}
    </div>
  );
}
